import { Moment } from 'moment';
import Log from '../models/Log';
import Comment from '../models/Comment';
import type { Thread } from '../models';

/** Max gap between consecutive burstable logs to keep them in the same burst. */
export const ACTIVITY_BURST_WINDOW_MS = 15 * 60 * 1000;

export type GroupedActivityLog = {
    log: Log;
    count: number;
    createdAt: Moment;
    /**
     * When present (length >= 2), this entry is a multi-action burst
     * for the same author + change, collapsed by default in LastActivity.
     */
    members?: GroupedActivityLog[];
};

const BURSTABLE_TYPES = new Set([
    Log.TYPE_CHANGE_CREATE,
    Log.TYPE_CHANGE_PATCH,
    Log.TYPE_CHANGE_ESTIMATED,
    Log.TYPE_CHANGE_UNESTIMATED,
    Log.TYPE_CHANGE_ASSIGNED,
    Log.TYPE_CHANGE_UNASSIGNED,
    Log.TYPE_CHANGE_PLANNED,
    Log.TYPE_CHANGE_UNPLANNED,
    Log.TYPE_CHANGE_STARTED,
    Log.TYPE_CHANGE_SPRINT_ADDED,
    Log.TYPE_CHANGE_SPRINT_REMOVED,
    Log.TYPE_CHANGE_RELATED_ADDED,
    Log.TYPE_CHANGE_RELATED_REMOVED,
    Log.TYPE_CHANGE_DISCARDED,
    Log.TYPE_CHANGE_RESTORED,
]);

const HIDDEN_CARD_LOG_TYPES = new Set([
    Log.TYPE_COMMENT_CREATE,
    Log.TYPE_COMMENT_UPDATE,
]);

/**
 * Build the card activity timeline: messages + logs, newest-first.
 * Logs that fall after a root message (until the next root) are nested
 * under that message (level = 1), so activity reads as imbriquée with messages.
 */
export function buildChangeActivityTimeline(options: {
    comments?: Comment[];
    logs?: Log[];
    includeLogs?: boolean;
    hideInternalComments?: boolean;
}): Array<Comment | Log> {
    const {
        comments = [],
        logs = [],
        includeLogs = true,
        hideInternalComments = false,
    } = options;

    let threads = Comment.toThreads(comments);
    if (hideInternalComments) {
        threads = threads
            .map((thread: Thread) => ({
                ...thread,
                comments: thread.comments.filter((item: Comment) => item.isReply()),
            }))
            .filter((thread: Thread) => thread.comments.length > 0);
    }

    const threadedComments = threads.flatMap((thread: Thread) => thread.comments);
    const roots = threadedComments
        .filter((comment: Comment) => (comment.level || 0) === 0)
        .slice()
        .sort((a: Comment, b: Comment) => a.createdAt.valueOf() - b.createdAt.valueOf());

    const items: Array<Comment | Log> = [...threadedComments];

    if (includeLogs) {
        logs
            .filter((log: Log) => !HIDDEN_CARD_LOG_TYPES.has(log.type))
            .forEach((log: Log) => {
                let parent: Comment | undefined;
                for (const root of roots) {
                    if (root.createdAt.valueOf() <= log.createdAt.valueOf())
                        parent = root;
                    else
                        break;
                }
                // Transient UI indent for ChangeActivity (not persisted).
                log.level = parent ? 1 : 0;
                items.push(log);
            });
    }

    return items.sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf());
}

/**
 * Group consecutive activity logs that share the same semantic fingerprint.
 * The input order is preserved (newest-first timelines stay readable).
 */
export function groupConsecutiveActivityLogs(logs: Log[]): GroupedActivityLog[] {
    const grouped: GroupedActivityLog[] = [];

    logs.forEach((log) => {
        const key = log.groupKey();
        const previous = grouped[grouped.length - 1];

        if (previous && previous.log.groupKey() === key) {
            previous.count += 1;
            if (log.createdAt.isAfter(previous.createdAt))
                previous.createdAt = log.createdAt;
            return;
        }

        grouped.push({
            log,
            count: 1,
            createdAt: log.createdAt,
        });
    });

    return grouped;
}

export type GroupActivityBurstsOptions = {
    /**
     * When true (default), only burst logs that share the same change id
     * (dashboard / LastActivity). Set false on a card timeline where all logs
     * already belong to one change and may omit an embedded change payload.
     */
    requireSameChange?: boolean;
};

/**
 * Collapse consecutive same-author / same-change burstable entries that are
 * close in time into a single expandable burst (for LastActivity / ChangeActivity).
 */
export function groupActivityBursts(
    entries: GroupedActivityLog[],
    windowMs: number = ACTIVITY_BURST_WINDOW_MS,
    options: GroupActivityBurstsOptions = {}
): GroupedActivityLog[] {
    const result: GroupedActivityLog[] = [];

    entries.forEach((entry) => {
        const previous = result[result.length - 1];
        const previousTail = previous?.members?.length
            ? previous.members[previous.members.length - 1]
            : previous;

        if (previous && previousTail && canBurstWith(previousTail, entry, windowMs, options)) {
            if (!previous.members) {
                previous.members = [{
                    log: previous.log,
                    count: previous.count,
                    createdAt: previous.createdAt,
                }];
            }
            previous.members.push(entry);
            return;
        }

        result.push({
            log: entry.log,
            count: entry.count,
            createdAt: entry.createdAt,
            ...(entry.members ? { members: entry.members } : {}),
        });
    });

    return result.map((entry) => {
        if (!entry.members || entry.members.length < 2) {
            const { members: _members, ...rest } = entry;
            return rest;
        }
        return entry;
    });
}

/**
 * Apply bursts to consecutive grouped-log runs inside a mixed timeline
 * (comments break bursts; non-log items pass through unchanged).
 */
export function groupActivityBurstsInItems<T>(
    items: Array<T | GroupedActivityLog>,
    windowMs: number = ACTIVITY_BURST_WINDOW_MS,
    options: GroupActivityBurstsOptions = {}
): Array<T | GroupedActivityLog> {
    const result: Array<T | GroupedActivityLog> = [];
    let pendingLogs: GroupedActivityLog[] = [];

    const flushPending = () => {
        if (!pendingLogs.length)
            return;
        result.push(...groupActivityBursts(pendingLogs, windowMs, options));
        pendingLogs = [];
    };

    items.forEach((item) => {
        if (isGroupedActivityLog(item)) {
            pendingLogs.push(item);
            return;
        }
        flushPending();
        result.push(item);
    });
    flushPending();
    return result;
}

/**
 * Group consecutive logs inside a mixed timeline (comments + logs).
 * Comments break log groups; unknown items are passed through unchanged.
 */
export function groupConsecutiveActivityItems<T>(items: T[]): Array<T | GroupedActivityLog> {
    const grouped: Array<T | GroupedActivityLog> = [];
    let pending: GroupedActivityLog | null = null;

    const flushPending = () => {
        if (!pending)
            return;
        grouped.push(pending);
        pending = null;
    };

    items.forEach((item) => {
        if (!(item instanceof Log)) {
            flushPending();
            grouped.push(item);
            return;
        }

        const key = item.groupKey();
        if (pending && pending.log.groupKey() === key) {
            pending.count += 1;
            if (item.createdAt.isAfter(pending.createdAt))
                pending.createdAt = item.createdAt;
            return;
        }

        flushPending();
        pending = {
            log: item,
            count: 1,
            createdAt: item.createdAt,
        };
    });

    flushPending();
    return grouped;
}

export function isGroupedActivityLog(item: unknown): item is GroupedActivityLog {
    return Boolean(
        item &&
        typeof item === 'object' &&
        'log' in item &&
        'count' in item &&
        (item as GroupedActivityLog).log instanceof Log
    );
}

export function isActivityBurst(entry: GroupedActivityLog): boolean {
    return Boolean(entry.members && entry.members.length >= 2);
}

export function burstActionCount(entry: GroupedActivityLog): number {
    if (!isActivityBurst(entry) || !entry.members)
        return entry.count;
    return entry.members.reduce((sum, member) => sum + member.count, 0);
}

function isBurstable(log: Log): boolean {
    if (!log?.type)
        return false;
    if (log.type.startsWith('ai:'))
        return true;
    return BURSTABLE_TYPES.has(log.type);
}

function canBurstWith(
    newer: GroupedActivityLog,
    older: GroupedActivityLog,
    windowMs: number,
    options: GroupActivityBurstsOptions = {}
): boolean {
    const newerLog = newer.log;
    const olderLog = older.log;
    const requireSameChange = options.requireSameChange !== false;

    if (requireSameChange) {
        if (!newerLog.change?._id || !olderLog.change?._id)
            return false;
        if (newerLog.change._id !== olderLog.change._id)
            return false;
    }
    if ((newerLog.author?._id || '') !== (olderLog.author?._id || ''))
        return false;
    if (!isBurstable(newerLog) || !isBurstable(olderLog))
        return false;
    if (!newer.createdAt.isSame(older.createdAt, 'day'))
        return false;

    return Math.abs(newer.createdAt.diff(older.createdAt)) <= windowMs;
}
